import { useMemo } from 'react';

import { Products } from '@site-mate/sitemate-global-shared';
import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useSearchParams } from 'react-router-dom';

import { groupWorkspacesIntoAccounts } from '@/components/workspace-switcher/workspace-switcher-menu.utils';
import { usePermissions } from '@/hooks/use-permissions';
import { useWorkspaceCreatePermissions } from '@/hooks/use-workspace-create-permissions';
import { getCreateWorkspaceAccountQueryOptions, getWorkspaceSwitcherWorkspacesQueryOptions } from '@/queries/workspace-switcher';
import { useUserStore } from '@/stores/user.store';

export default function CreateWorkspaceRoute() {
  const { user } = useUserStore();
  const { isSuperUser } = usePermissions();

  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('accountId');

  const workspacesQuery = useQuery({
    ...getWorkspaceSwitcherWorkspacesQueryOptions(),
    enabled: !!user && !isSuperUser,
  });

  const accounts = useMemo(() => (workspacesQuery.data ? groupWorkspacesIntoAccounts(workspacesQuery.data) : []), [workspacesQuery.data]);

  const {
    permissionsByAccount,
    accountsByAccountId,
    isLoading: isLoadingPermissions,
  } = useWorkspaceCreatePermissions({
    accounts,
    isSuperUser,
    enabled: !!user && !isSuperUser,
  });

  const superUserAccountQuery = useQuery({
    ...getCreateWorkspaceAccountQueryOptions({ accountId }),
    enabled: isSuperUser && !!accountId,
  });

  const isWaitingForNonSuperUserData = !isSuperUser && (workspacesQuery.isLoading || isLoadingPermissions);
  const isWaitingForSuperUserAccount = isSuperUser && !!accountId && superUserAccountQuery.isLoading;
  const isLoading = isWaitingForNonSuperUserData || isWaitingForSuperUserAccount;

  if (isLoading) {
    return null;
  }

  // Unreachable behind the auth guard above this route, but if reached, deny rather than render.
  if (!user) {
    return <Navigate to="/404" replace />;
  }

  if (isSuperUser) {
    if (accountId && (superUserAccountQuery.isError || !superUserAccountQuery.data)) {
      return <Navigate to="/404" replace />;
    }
    const account = accountId ? (superUserAccountQuery.data ?? null) : null;
    return <Outlet context={{ account }} />;
  }

  if (!accountId) {
    return <Navigate to="/404" replace />;
  }

  const gearbeltPermission = permissionsByAccount.get(accountId)?.[Products.Gearbelt];
  if (!gearbeltPermission?.canCreate || gearbeltPermission.isDisabled) {
    return <Navigate to="/404" replace />;
  }

  const account = accountsByAccountId.get(accountId) ?? null;
  return <Outlet context={{ account }} />;
}
